import { forwardRef, type InputHTMLAttributes } from 'react'
import { AlertCircle, CheckCircle } from 'lucide-react'
import { cn } from '@/lib/utils/cn'

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string
  error?: string
  hint?: string
  leftIcon?: React.ReactNode
  rightIcon?: React.ReactNode
  showValidIcon?: boolean
  isValid?: boolean
  theme?: 'adaptive' | 'light'
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ className, label, error, hint, leftIcon, rightIcon, id, required, showValidIcon, isValid, theme = 'adaptive', ...props }, ref) => {
    const inputId = id || label?.toLowerCase().replace(/\s+/g, '-')
    const showSuccessIcon = showValidIcon && isValid && !error

    return (
      <div className="w-full">
        {label && (
          <label
            htmlFor={inputId}
            className={cn(
              'mb-1.5 block text-sm font-medium',
              theme === 'light' ? 'text-gray-700' : 'text-gray-700 dark:text-gray-300'
            )}
          >
            {label}
            {required && <span className="text-error-500 ml-0.5">*</span>}
          </label>
        )}
        <div className="relative">
          {leftIcon && (
            <div
              className={cn(
                'absolute inset-y-0 left-0 flex items-center pl-3 text-gray-400 pointer-events-none',
                theme === 'light' ? '' : 'dark:text-gray-500'
              )}
            >
              {leftIcon}
            </div>
          )}
          <input
            ref={ref}
            id={inputId}
            data-theme={theme}
            required={required}
            aria-invalid={error ? 'true' : 'false'}
            aria-describedby={error ? `${inputId}-error` : hint ? `${inputId}-hint` : undefined}
            className={cn(
              'w-full rounded-lg border bg-white px-4 py-3 text-gray-900 placeholder-gray-400',
              theme === 'light' ? '' : 'dark:bg-gray-900 dark:text-gray-100 dark:placeholder-gray-500',
              'transition-colors duration-200',
              theme === 'light' ? 'hover:border-gray-300' : 'hover:border-gray-300 dark:hover:border-gray-600',
              'focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 focus:outline-none',
              error
                ? 'border-error-500 focus:border-error-500 focus:ring-error-500/20 pr-10'
                : showSuccessIcon
                ? 'border-success-500 pr-10'
                : theme === 'light'
                  ? 'border-gray-200'
                  : 'border-gray-200 dark:border-gray-700',
              leftIcon && 'pl-10',
              rightIcon && !error && !showSuccessIcon && 'pr-10',
              className
            )}
            {...props}
          />
          {/* Validation icons */}
          {error && (
            <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
              <AlertCircle className="h-5 w-5 text-error-500" />
            </div>
          )}
          {showSuccessIcon && (
            <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
              <CheckCircle className="h-5 w-5 text-success-500" />
            </div>
          )}
          {rightIcon && !error && !showSuccessIcon && (
            <div
              className={cn(
                'absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400',
                theme === 'light' ? '' : 'dark:text-gray-500'
              )}
            >
              {rightIcon}
            </div>
          )}
        </div>
        {error && (
          <p id={`${inputId}-error`} className="mt-1.5 text-sm text-error-500 flex items-center gap-1">
            <span>{error}</span>
          </p>
        )}
        {hint && !error && (
          <p
            id={`${inputId}-hint`}
            className={cn(
              'mt-1.5 text-sm text-gray-500',
              theme === 'light' ? '' : 'dark:text-gray-400'
            )}
          >
            {hint}
          </p>
        )}
      </div>
    )
  }
)

Input.displayName = 'Input'
